import React, { FC, useState, useEffect } from 'react'
import first from 'lodash/first'
import size from 'lodash/size'
import get from 'lodash/get'
import debounce from 'lodash/debounce'
import { useDropzone, FileRejection, FileError } from 'react-dropzone'
import { Icon, LinkButton } from '@ui'
import { IUploadedImageAsset } from '@api'
import cx from 'classnames'
import css from './uploadImages.module.scss'
import {
  MAX_SIZE,
  getMaxFilesByUploadedImages,
  getErrorMessageByErrorCode,
} from './utils/dragNdropUtils'
import {
  validatorTooManyFiles,
  validatorExistingFile,
  getDefaultFileError,
} from './utils/customValidation'
import { UploadingProgress } from '../../hooks/useProgress'
import { ImagesProgressBar } from '../ImagesProgressBar/ImagesProgressBar'
type UploadImagesProps = {
  uploadedImages: IUploadedImageAsset[]
  spaceId: string
  isAssetsLoading: boolean
  progress: UploadingProgress
  onDrop: (files: File[], fileRejections: FileRejection[]) => void
}

const UploadImages: FC<React.PropsWithChildren<UploadImagesProps>> = ({
  onDrop,
  spaceId,
  isAssetsLoading,
  progress,
  uploadedImages,
}) => {
  const maxFiles = getMaxFilesByUploadedImages(size(uploadedImages))
  const [error, setError] = useState<FileError | null>(null)
  const [isDragged, setIsDragged] = useState(false)

  const debouncedSetIsDragged = debounce(setIsDragged, 80)

  const resetError = () => setError(null)

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles,
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpg': ['.jpeg', '.jpg'],
      'application/pdf': ['.pdf'],
    },
    multiple: true,
    disabled: isAssetsLoading,
    maxSize: MAX_SIZE,
    validator: (file) => {
      // custom validators, next validators should be put with || operator.
      // e.g. someValidator() || someOtherValidator()
      return validatorTooManyFiles(maxFiles) || validatorExistingFile(file, uploadedImages)
    },
    onDropRejected: (fileRejections) => {
      const error: FileError = get(first(fileRejections), 'errors[0]', { code: '', message: '' })
      const defaultError = getDefaultFileError(error)
      setError(defaultError || error)
    },
    onDragOver: resetError,
    onFileDialogOpen: resetError,
    onDragEnter: () => debouncedSetIsDragged(true),
    onDragLeave: () => debouncedSetIsDragged(false),
  })

  const hasError = !!error

  useEffect(() => {
    resetError()
  }, [spaceId])

  return progress.isUploading ? (
    <ImagesProgressBar
      progressPercent={progress.progressPercent}
      currentFilesCount={progress.currentFilesCount}
      totalFilesCount={progress.totalFilesCount}
    />
  ) : (
    <div
      data-test="upload-images-dropzone"
      className={cx(css.dropzone, {
        [css.dropzone_error]: hasError,
        [css.dragged]: isDragged,
      })}
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      {hasError ? renderTextWithError(error, resetError) : renderText()}
    </div>
  )
}

const renderText = () => (
  <>
    <Icon light name="folder-image" className={cx('fa-light', css.image_icon)} />
    <div className={css.link_button_wrapper}>
      Drag to upload, or <LinkButton data-test="upload-images-browse-button" text="Browse" />
    </div>
    <p>Maximum of 10 files no larger than 5 MB</p>
    <p>in JPEG, PNG, or PDF.</p>
  </>
)

const renderTextWithError = (error: FileError | null, onLinkClick: () => void) => {
  return (
    <>
      <Icon light name="triangle-exclamation" className={cx('fa-light', css.image_icon)} />
      <div className={css.link_button_wrapper}>{error?.message}</div>
      <p>
        <LinkButton onClick={onLinkClick} text="Browse" /> in JPEG, PDF, or PNG, 5 MB.
      </p>
      <p> or smaller. Upload 10 files or less.</p>
    </>
  )
}

export default UploadImages
